// @ts-nocheck
import React from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { useModalContext } from "components/modal";
import { useSnackbarContext } from "components/snackbar";
import Spinner from "components/spinner";
import { Button, OutlineButton } from "components/button";
import StatusTag from "components/tag/indexv2";
import Placeholder from "views/request/component/placeholder";
import DollarInputV2 from "components/input/dollarInputV2";
import NumberFormat from "react-number-format";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "contexts/auth";
import { TInvoice } from "types/invoice";
import { TPayment } from "types/payment";
import { RiCheckboxBlankLine, RiCheckboxFill } from "react-icons/ri";
import { dollarFormatter } from "utils";
import { cloneDeep } from "lodash";
import convertCVD from "utils/convertCVD";


interface unapplyPaymentModalProps {
	data: TPayment;
	type?: "invoice";
	setUnapplyingPaymentId?: (id: number) => void;
}


const UnapplyPaymentModal = ({ data, type = "customer", setUnapplyingPaymentId }: unapplyPaymentModalProps) => {
	const { t } = useTranslation();
	const tBase = "containers.payments.unapplyPaymentModal";
	const tr = (key: string) => t(`${tBase}.${key}`);
	const { cvd_type } = useAuthContext();
	const modal = useModalContext();
	const snackbar = useSnackbarContext();
	const queryClient = useQueryClient();

	const [step, setStep] = React.useState<"prompt" | "form">("prompt");
	const [unapplyMode, setUnapplyMode] = React.useState<"full" | "partial">("full");
	const [showConfirmation, setShowConfirmation] = React.useState(false);
	const [showCreditModal, setShowCreditModal] = React.useState(false);
	const [showCreditConfirmation, setShowCreditConfirmation] = React.useState(false);
	const [creditMode, setCreditMode] = React.useState<"full" | "partial">("full");
	const [selected, setSelected] = React.useState<
		{ invoiceId: string; amount: number | undefined }[] | null
	>(null);

	const parentId =
		type === "request"
			? data.fk_request_id
			: type === "customer"
			? data.fk_bcustomer_id
			: data.fk_project_id;

	const {
		data: invoices,
		isLoading,
		refetch,
	} = useQuery<TInvoice[] | null>(
		["invoices", "unapply", parentId, type],
		async () =>
			await axios
				.get(
					`${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoices/deposit?type=${type}&id=${parentId}`
				)
				.then((res) => res.data),
		{ enabled: false }
	);

	// Fetch payment data to get the actual breakdown
	const {
		data: paymentData,
		isLoading: isLoadingPayment,
	} = useQuery<TPayment | null>(
		["payment", data.id],
		async () =>
			await axios
				.get(
					`${process.env.REACT_APP_SERVER_URL}/api/v1/company/payments/${data.id}`
				)
				.then((res) => res.data),
		{ enabled: !!data.id }
	);

	// Fetch invoice data to get the total amount
	const {
		data: invoiceData,
		isLoading: isLoadingInvoice,
	} = useQuery<TInvoice | null>(
		["invoice", data.fk_invoice_id],
		async () =>
			await axios
				.get(
					`${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${data.fk_invoice_id}`
				)
				.then((res) => res.data),
		{ enabled: !!data.fk_invoice_id }
	);


	type UnapplyPayload = {
		selected: { invoiceId: string; amount: number | undefined }[] | null;
		reapply: boolean;
		unapplyMode: "full" | "partial";
		isCredit?: boolean;
	};

	const mutation = useMutation(
		async (payload?: UnapplyPayload) => {
			const body = payload
				? { selected: payload.selected?.map(s => ({ invoiceId: s.invoiceId, amount: s.amount })), reapply: payload.reapply, unapplyMode: payload.unapplyMode }
				: (step === "prompt"
					? { selected: selected?.map(s => ({ invoiceId: s.invoiceId, amount: s.amount })), reapply: false, unapplyMode }
					: { selected: selected?.map(s => ({ invoiceId: s.invoiceId, amount: s.amount })), reapply: true, unapplyMode });
			return await axios
				.post(
					`${process.env.REACT_APP_SERVER_URL}/api/v1/company/payments/unapply/${data.id}`,
					body
				)
				.then((res) => res.data);
		},
		{
			onSuccess: (_data, variables) => {
				const isCredit = variables?.isCredit ?? step === "prompt";
				const message = isCredit
					? tr("Payment Credited to Customer Account")
					: tr("Payment Unapplied from Invoice");
				// Close all modals (current + any parent)
				modal.setModal();
				modal.setModal();
				modal.setModal();
				snackbar.showSnackbar(message);
				queryClient.invalidateQueries();
				setUnapplyingPaymentId?.(null);
			},
			onError: () => {
			},
		}
	);

	const handleCreditToCustomer = () => {
		// Show credit modal to let user choose full or partial
		setShowCreditModal(true);
		// Auto-initialize with full credit mode since it's the default
		if (creditMode === "full") {
			setSelected([{ 
				invoiceId: String(data.fk_invoice_id),
				amount: data.used_amount / 100
			}]);
		}
	};

	// Calculate total amount to unapply
	const totalAmountToUnapply = data.used_amount / 100;
	
	// Calculate total amount currently allocated
	const totalAllocated = selected?.reduce((sum, item) => sum + (item.amount || 0), 0) || 0;
	
	// Calculate remaining amount
	const remainingAmount = totalAmountToUnapply - totalAllocated;

	// Auto-distribute remaining amount equally among selected invoices
	// const handleAutoDistribute = () => {
	// 	if (!selected || selected.length === 0 || remainingAmount <= 0) return;
		
	// 	const equalAmount = remainingAmount / selected.length;
	// 	const updatedSelected = selected.map(item => ({
	// 		...item,
	// 		amount: (item.amount || 0) + equalAmount
	// 	}));
		
	// 	setSelected(updatedSelected);
	// };

	// Note: Removed handleFullPaymentUnapply function as auto-distribution is no longer used

	// Handle mode change: uncheck all invoices and clear amounts when switching Full/Partial
	const handleModeChange = (mode: "full" | "partial") => {
		setUnapplyMode(mode);
		setSelected(null);
	};

	// Handle proceed button click with confirmation for full payment mode
	const handleProceedClick = () => {
		if (unapplyMode === "full" && remainingAmount > 0) {
			setShowConfirmation(true);
		} else {
			mutation.mutate();
		}
	};

	// Handle confirmation for full payment with remaining amount — close all modals (including parent) when confirming
	const handleConfirmProceed = () => {
		setShowConfirmation(false);
		modal.setModal();
		modal.setModal();
		mutation.mutate();
	};

	// Handle cancel confirmation
	const handleCancelConfirmation = () => {
		setShowConfirmation(false);
	};

	// Handle credit mode change
	const handleCreditModeChange = (mode: "full" | "partial") => {
		setUnapplyMode(mode);
		setCreditMode(mode);
		
		// If full credit mode, automatically add the invoice to selected array
		if (mode === "full") {
			setSelected([{ 
				invoiceId: String(data.fk_invoice_id),
				amount: data.used_amount / 100
			}]);
		} else {
			// For partial mode, clear the selection to allow manual entry
			setSelected([{ 
				invoiceId: String(data.fk_invoice_id),
				amount: undefined
			}]);
		}
	};

	// Handle credit confirmation
	const handleConfirmCredit = () => {
		setShowCreditModal(false);
		setShowCreditConfirmation(true);
	};

	// Handle cancel credit modal
	const handleCancelCredit = () => {
		setShowCreditModal(false);
		modal.setModal();
	};

	// Handle credit confirmation proceed — close all modals immediately so parent prompt doesn't reappear
	const handleCreditConfirmationProceed = () => {
		setShowCreditConfirmation(false);
		setShowCreditModal(false);
		const creditAmount = creditMode === "full" ? data.used_amount / 100 : selected?.[0]?.amount;
		const payload: UnapplyPayload = {
			selected: [{ invoiceId: String(data.fk_invoice_id), amount: creditAmount }],
			reapply: false,
			unapplyMode,
			isCredit: true,
		};
		setUnapplyingPaymentId?.(data.id);
		// Close modals first so user doesn't see the prompt step again
		modal.setModal();
		modal.setModal();
		mutation.mutate(payload);
	};

	// Handle cancel credit confirmation
	const handleCancelCreditConfirmation = () => {
		setShowCreditConfirmation(false);
	};

	// Note: Removed auto-distribution logic for full payment mode
	// Users now manually enter amounts in both full and partial modes

	if (isLoading) return <Spinner />;

	// Validation: Proceed button enabled if at least one invoice is selected
	const canProceed = selected && selected.length > 0;
	
	// In full payment mode, we need at least one selected invoice
	// const isValidForFullMode = unapplyMode === "full" ? selected && selected.length > 0 : true;

	const populateInvoices = invoices?.map((invoice) => {
		const index = selected ? selected.findIndex((s) => s.invoiceId === invoice.id) : -1;
		const isSelected = index !== -1;
		
		// Check if this invoice is the one the payment is currently applied to
		const isCurrentlyApplied = String(data.fk_invoice_id) === String(invoice.id);
		
		// Check if there's no remaining amount available for new selections
		const currentTotalAllocated = selected?.reduce((sum, item) => sum + (item.amount || 0), 0) || 0;
		const hasNoRemainingAmount = currentTotalAllocated >= totalAmountToUnapply && !isSelected;
		
		// Check if there are any manually selected invoices with empty amounts (prevents multiple selections with empty amounts)
		// Exclude the default pre-selected invoice (currently applied invoice) from this check
		const hasEmptyAmounts = selected && selected.length > 0 ? selected.some(item => {
			const isDefaultInvoice = String(item.invoiceId) === String(data.fk_invoice_id);
			return !isDefaultInvoice && (!item.amount || item.amount === 0);
		}) : false;
		const cannotSelectDueToEmptyAmounts = hasEmptyAmounts && !isSelected;

		// Calculate maximum allowed unapply amount
		const maxUnapplyAmount = Math.min(data.used_amount / 100, invoice.amount_due / 100);
		const currentUnapplyAmount = isSelected && selected ? (selected[index].amount || 0) : 0;
		const newUnpaidAmount = (invoice.amount_due / 100) - currentUnapplyAmount;
		
		// Calculate current total allocated (excluding this invoice)
		const currentTotalAllocatedExcludingThis = selected?.reduce((sum, item, idx) => {
			if (idx !== index) {
				return sum + (item.amount || 0);
			}
			return sum;
		}, 0) || 0;
		
		// Calculate maximum allowed for this invoice (prevents negative remaining)
		const maxForThisInvoice = Math.min(
			maxUnapplyAmount,
			totalAmountToUnapply - currentTotalAllocatedExcludingThis
		);
		
		const isAmountValid = newUnpaidAmount >= 0 && currentUnapplyAmount <= maxForThisInvoice && currentUnapplyAmount > 0;

		const statusColor = convertCVD(invoice.status.color, cvd_type);

		return (
			<OutlineButton
				key={invoice.id}
				onClick={() => {
					// Prevent selection if this invoice is currently applied to
					if (isCurrentlyApplied) return;
					
					// Prevent selection if there's no remaining amount available
					if (hasNoRemainingAmount) return;
					
					// Prevent selection if there are empty amounts in other selected invoices
					if (cannotSelectDueToEmptyAmounts) return;
					
					setSelected((s) => {
						let temp = cloneDeep(s);

						if (!temp) return [{ invoiceId: invoice.id, amount: undefined }];

						let index = temp.findIndex((s) => s.invoiceId === invoice.id);

						if (index === -1) {
							// Check if there are any manually selected invoices with empty amounts
							// Exclude the default pre-selected invoice (currently applied invoice) from this check
							const hasEmptyAmounts = temp.some(item => {
								const isDefaultInvoice = String(item.invoiceId) === String(data.fk_invoice_id);
								return !isDefaultInvoice && (!item.amount || item.amount === 0);
							});
							if (hasEmptyAmounts) return temp; // Don't allow new selection
							
							temp.push({ invoiceId: invoice.id, amount: undefined });
							return temp;
						} else {
							temp.splice(index, 1);
							if (temp.length === 0) return null;
							return temp;
						}
					})
				}}
				className={`!relative !flex !flex-col !items-stretch !h-auto !p-2 ${
					isSelected && "!border-primary"
				} ${(isCurrentlyApplied || hasNoRemainingAmount || cannotSelectDueToEmptyAmounts) ? "!opacity-50 !cursor-not-allowed" : ""}`}
				disabled={isCurrentlyApplied || hasNoRemainingAmount || cannotSelectDueToEmptyAmounts}
			>
				<div className="space-x-2">
					<span>
						{tr("Invoice")} #{invoice.id}
						{isCurrentlyApplied && (
							<span className="ml-2 text-xs text-orange-600 font-medium">
								({tr("Currently Applied")})
							</span>
						)}
					</span>
					<StatusTag color={statusColor} size="xxsmall">
						{tr(invoice.status.label)}
					</StatusTag>
				</div>
				<div className="flex flex-col space-y-1">
					<span className="text-xl">{dollarFormatter(invoice.applied_amount)}</span>
					<span className="text-sm text-gray-500">
						{tr("Unpaid")}: {dollarFormatter(invoice.amount_due)}
					</span>
					{isSelected && selected && selected[index]?.amount && (
						<span className={`text-sm font-medium ${
							isAmountValid ? 'text-blue-600' : 'text-red-600'
						}`}>
							{tr("New Unpaid")}: {dollarFormatter(invoice.amount_due - ((selected[index].amount || 0) * 100))}
							{!isAmountValid && (
								<span className="text-xs ml-1">
									{currentUnapplyAmount === 0 ? tr("Amount must be greater than $0.00") :
									 newUnpaidAmount < 0 ? tr("Cannot go below $0.00") : 
									 tr("Exceeds maximum amount")}
								</span>
							)}
						</span>
					)}
				</div>
				{isSelected && !isCurrentlyApplied && !hasNoRemainingAmount && (
					<DollarInputV2
						autoFocus
						$size="small"
						onClick={(e) => e.stopPropagation()}
						value={selected ? selected[index].amount : ""}
						onValueChange={(e) => {
							// Limit the input to the maximum allowed amount and prevent zero
							const inputAmount = e.floatValue || 0;
							
							// Recalculate the maximum allowed for this invoice based on current state
							const currentTotalAllocated = selected?.reduce((sum, item, idx) => {
								if (idx !== index) {
									return sum + (item.amount || 0);
								}
								return sum;
							}, 0) || 0;
							
							const maxForThisInvoice = Math.min(
								maxUnapplyAmount,
								totalAmountToUnapply - currentTotalAllocated
							);
							
							// Limit the input to prevent negative remaining amount
							const limitedAmount = Math.min(inputAmount, maxForThisInvoice);
							
							// Only set the amount if it's greater than 0 and doesn't exceed the limit
							const finalAmount = limitedAmount > 0 ? limitedAmount : undefined;
							
							setSelected((s) => {
								if (s) {
									let temp = cloneDeep(s);
									temp[index].amount = finalAmount;
									return temp;
								}
								return s;
							});
						}}
						placeholder={`${tr("Unapply Amt($)")} (max: ${dollarFormatter(Math.min(maxUnapplyAmount, totalAmountToUnapply - (selected?.reduce((sum, item, idx) => idx !== index ? sum + (item.amount || 0) : sum, 0) || 0)) * 100)})`}
						className={`${!isAmountValid ? "!border-red-500" : ""}`}
					/>
				)}
				<div
					className={`absolute top-0 right-0 m-2 text-lg ${
						(isCurrentlyApplied || hasNoRemainingAmount || cannotSelectDueToEmptyAmounts)
							? "text-gray-300" 
							: isSelected 
								? "text-[#ff4f00]" 
								: "text-slate-500"
					}`}
				>
					{(isCurrentlyApplied || hasNoRemainingAmount || cannotSelectDueToEmptyAmounts) ? (
						<RiCheckboxBlankLine />
					) : isSelected ? (
						<RiCheckboxFill />
					) : (
						<RiCheckboxBlankLine />
					)}
				</div>
			</OutlineButton>
		);
	});

	return (
		<div className="space-y-4 max-w-[500px]">
			{step === "prompt" ? (
				<>
					<h2>{tr("Choose what to do with the payment amount")}</h2>
					<div className="flex justify-end space-x-4">
						<Button
							primary
							onClick={async () => {
								setStep("form");
								await refetch();
							}}
						>
							{tr("Apply to Another Invoice")}
						</Button>
						<OutlineButton
							onClick={handleCreditToCustomer}
						>
							{tr("Credit to Customer Account")}
						</OutlineButton>
					</div>
				</>
			) : (
				<>
					{/* Unapply Mode Toggle */}
					<div className="bg-blue-50 p-4 rounded-lg space-y-3">
						<div className="flex items-center justify-between">
							<span className="font-medium text-gray-700">{tr("Unapply Mode")}:</span>
							<div className="flex bg-gray-200 rounded-lg p-1">
								<button
									onClick={() => handleModeChange("partial")}
									className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
										unapplyMode === "partial"
											? "bg-white text-blue-600 shadow-sm"
											: "text-gray-600 hover:text-gray-800"
									}`}
								>
									{tr("Partial Payment")}
								</button>
								<button
									onClick={() => handleModeChange("full")}
									className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
										unapplyMode === "full"
											? "bg-white text-blue-600 shadow-sm"
											: "text-gray-600 hover:text-gray-800"
									}`}
								>
									{tr("Full Payment")}
								</button>
							</div>
						</div>
						<div className="text-sm text-gray-600 h-[50px]">
							{unapplyMode === "full" 
								? tr("Full Payment reapplies the full payment amount to the selected invoices. Any remaining amount will be credited to the customer's account.")
								: tr("Partial Payment reapplies a partial payment amount to the selected invoices. Any remaining amount will remain on the invoice.")
							}
						</div>
					</div>
					
					{/* Amount tracking section - always visible */}
					<div className="bg-gray-50 p-4 rounded-lg space-y-2">
						<div className="flex justify-between items-center">
							<span className="font-medium">{tr("Total to Unapply")}:</span>
							<span className="font-bold text-lg">{dollarFormatter(totalAmountToUnapply * 100)}</span>
						</div>
						<div className="flex justify-between items-center">
							<span className="font-medium">{tr("Allocated")}:</span>
							<span className={`font-bold ${totalAllocated > totalAmountToUnapply ? 'text-red-600' : 'text-green-600'}`}>
								{dollarFormatter(totalAllocated * 100)}
							</span>
						</div>
						<div className="flex justify-between items-center">
							<span className="font-medium">{tr("Remaining")}:</span>
							<span className={`font-bold ${remainingAmount < 0 ? 'text-red-600' : remainingAmount > 0 ? 'text-orange-600' : 'text-green-600'}`}>
								{dollarFormatter(remainingAmount * 100)}
							</span>
						</div>
						{/* {remainingAmount > 0 && selected?.length > 1 && unapplyMode === "partial" && (
							<Button
								onClick={handleAutoDistribute}
								className="w-full mt-2"
								$size="small"
							>
								{tr("Auto-Distribute Remaining Amount Equally")}
							</Button>
						)} */}
					</div>
					
					{invoices?.length ? (
						<div className="space-y-4">
							<div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4">
								<div className="flex flex-col space-y-2">{populateInvoices}</div>
							</div>
							<div className="flex justify-end space-x-4">
								<Button
									primary
									disabled={!canProceed}
									isLoading={mutation.isLoading}
									onClick={handleProceedClick}
								>
									{tr("Confirm")}
								</Button>
								<OutlineButton onClick={() => modal.setModal()}>
									{tr("Cancel")}
								</OutlineButton>
							</div>
						</div>
					) : (
						<Placeholder
							label={`${tr("No Eligible Invoices Found")} ${type}.`}
							header={tr("No Invoices")}
							type="invoice"
						/>
					)}
				</>
			)}

			{/* Confirmation Modal for Full Payment with Remaining Amount */}
			{showConfirmation && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg p-6 max-w-[500px] w-full mx-4">
						<div className="text-center">
							<h3 className="text-lg font-semibold text-gray-900 mb-4">
								{tr("Remaining Amount Credit")}
							</h3>
							<p className="text-gray-600 mb-6">
								{tr("There is a remaining amount of")} <span className="font-semibold text-orange-600">{dollarFormatter(remainingAmount * 100)}</span> {tr("that will be added to the customer's account as a credit.")}
							</p>
							<div className="flex justify-end space-x-3">
								<Button primary onClick={handleConfirmProceed}>
									{tr("Confirm")}
								</Button>
								<OutlineButton onClick={handleCancelConfirmation}>
									{tr("Cancel")}
								</OutlineButton>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Credit Modal for Full/Partial Credit Selection */}
			{showCreditModal && (
				<div className="fixed inset-0 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg p-6 max-w-[500px] w-full mx-4 shadow-2xl border">
						<div className="text-center">
							<h3 className="text-lg font-semibold text-gray-900 mb-4">
								{tr("Credit to Customer Account")}
							</h3>
							<p className="text-gray-600 mb-6">
								{tr("Choose how much to credit to the customer's account")}
							</p>
							
							{/* Payment Details */}
							<div className="bg-gray-50 p-4 rounded-lg space-y-2 mb-6 text-left">
								<div className="flex justify-between items-center">
									<span className="font-medium">{tr("Full Payment Amount")}:</span>
									<span className="font-bold text-lg">{dollarFormatter(paymentData?.amount || data.used_amount)}</span>
								</div>
								<div className="flex justify-between items-center">
									<span className="font-medium">{tr("Full Invoice Amount")}:</span>
									<span className="font-bold">{dollarFormatter(invoiceData?.actual_total || 0)}</span>
								</div>
								<div className="flex justify-between items-center">
									<span className="font-medium">{tr("Current Invoice Amount Due")}:</span>
									<span className="font-bold text-green-600">{dollarFormatter((invoiceData?.actual_total || 0) - data.used_amount)}</span>
								</div>
								<div className="flex justify-between items-center">
									<span className="font-medium">{tr("Currently Applied Amount")}:</span>
									<span className="font-bold text-blue-600">{dollarFormatter(paymentData?.used_amount || data.used_amount)}</span>
								</div>
							</div>
							
							{/* Credit Mode Toggle */}
							<div className="bg-blue-50 p-4 rounded-lg space-y-3 mb-6">
								<div className="flex items-center justify-between">
									<span className="font-medium text-gray-700">{tr("Credit Mode")}:</span>
									<div className="flex bg-gray-200 rounded-lg p-1">
										<button
											onClick={() => handleCreditModeChange("partial")}
											className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
												creditMode === "partial"
													? "bg-white text-blue-600 shadow-sm"
													: "text-gray-600 hover:text-gray-800"
											}`}
										>
											{tr("Partial Credit")}
										</button>
										<button
											onClick={() => handleCreditModeChange("full")}
											className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
												creditMode === "full"
													? "bg-white text-blue-600 shadow-sm"
													: "text-gray-600 hover:text-gray-800"
											}`}
										>
											{tr("Full Credit")}
										</button>
									</div>
								</div>
								<div className="text-sm text-gray-600">
									{creditMode === "full" 
										? tr("Full Credit will credit the entire payment amount to the customer's account.")
										: tr("Partial Credit allows you to specify a custom amount to credit to the customer's account.")
									}
								</div>
							</div>

							{/* Amount Input for Partial Credit */}
							{creditMode === "partial" && (
								<div className="mb-6">
									<label className="block text-sm font-medium text-gray-700 mb-2">
										{tr("Credit Amount")}
									</label>
									<DollarInputV2
										autoFocus
										$size="small"
										value={selected?.[0]?.amount || ""}
										onValueChange={(e) => {
											const inputAmount = e.floatValue || 0;
											const maxAmount = data.used_amount / 100.0;
											const limitedAmount = Math.min(inputAmount, maxAmount);
											const finalAmount = limitedAmount > 0 ? limitedAmount : undefined;
											setSelected([{ 
												invoiceId: data.fk_invoice_id,
												amount: finalAmount
											}]);
										}}
										placeholder={`${tr("Credit Amt($)")} (max: ${dollarFormatter(data.used_amount)})`}
										className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
									/>
								</div>
							)}

							<div className="flex justify-end space-x-3">
								<Button 
									primary 
									onClick={handleConfirmCredit}
									disabled={creditMode === "partial" && (!selected?.[0]?.amount || selected[0].amount < 0)}
								>
									{tr("Confirm")}
								</Button>
								<OutlineButton onClick={handleCancelCredit}>
									{tr("Cancel")}
								</OutlineButton>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Credit Confirmation Modal */}
			{showCreditConfirmation && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg p-6 max-w-[500px] w-full mx-4">
						<div className="text-center">
							<h3 className="text-lg font-semibold text-gray-900 mb-4">
								{tr("Confirm Credit to Customer Account")}
							</h3>
							<p className="text-gray-600 mb-6">
								{creditMode === "full" 
									? (
										<>
											{tr("Are you sure you want to credit the entire payment amount of")}{" "}
											<span className="font-semibold text-blue-600">{dollarFormatter(data.used_amount)}</span>{" "}
											{tr("to the customer's account?")}
										</>
									)
									: (
										<>
											{tr("Are you sure you want to credit")}{" "}
											<span className="font-semibold text-blue-600">{dollarFormatter((selected?.[0]?.amount || 0) * 100)}</span>{" "}
											{tr("to the customer's account?")}
										</>
									)
								}
							</p>
							<div className="flex justify-end space-x-3">
								<Button primary onClick={handleCreditConfirmationProceed}>
									{tr("Confirm")}
								</Button>
								<OutlineButton onClick={handleCancelCreditConfirmation}>
									{tr("Cancel")}
								</OutlineButton>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default UnapplyPaymentModal;
